import React, { useState, useEffect } from 'react';
import {
  HardDrive,
  Thermometer,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Activity,
  Server,
  Database,
  Wifi,
  Info,
  AlertTriangle,
  Circle,
  RefreshCw,
  Camera,
  Video,
  CalendarClock
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import axios from '../../utils/axiosDash';

const Nvr = () => {
  const [nvrData, setNvrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch NVR data
  const fetchNvrData = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get('/fetch-nvr-status');

      if (response.data.success) {
        setNvrData(response.data.data);
        setError(null);
      } else {
        setError(response.data.message || 'Failed to fetch NVR data');
      }
    } catch (err) {
      console.error('Error fetching NVR data:', err);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchNvrData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchNvrData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Format megabytes to human readable (hdd.capacity / hdd.free_space come back in MB)
  const formatBytes = (megabytes) => {
    if (!megabytes && megabytes !== 0) return '0 MB';
    if (megabytes < 1024) return `${megabytes} MB`;
    if (megabytes < 1048576) return `${(megabytes / 1024).toFixed(2)} GB`;
    return `${(megabytes / 1048576).toFixed(2)} TB`;
  };

  // Format a raw byte count (recording.*.data_bytes) to human readable
  const formatDataBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '0 GB';
    const gb = bytes / 1024 ** 3;
    if (gb < 1024) return `${gb.toFixed(2)} GB`;
    return `${(gb / 1024).toFixed(2)} TB`;
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      'ok': 'text-green-600 bg-green-50 border-green-200',
      'warning': 'text-yellow-600 bg-yellow-50 border-yellow-200',
      'error': 'text-red-600 bg-red-50 border-red-200',
      'unknown': 'text-gray-600 bg-gray-50 border-gray-200',
      'Healthy': 'text-green-600 bg-green-50 border-green-200',
      'Warning': 'text-yellow-600 bg-yellow-50 border-yellow-200',
      'Critical': 'text-red-600 bg-red-50 border-red-200'
    };
    return colors[status] || colors['unknown'];
  };

  // Get HDD status icon
  const getHddStatusIcon = (status) => {
    if (status === 'ok') return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (status === 'warning') return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    if (status === 'error') return <XCircle className="w-5 h-5 text-red-500" />;
    return <Info className="w-5 h-5 text-gray-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading NVR Status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto" />
          <p className="mt-4 text-red-600 font-semibold">{error}</p>
          <button
            onClick={fetchNvrData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!nvrData) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <Server className="w-16 h-16 text-gray-400 mx-auto" />
          <p className="mt-4 text-gray-600">No NVR data available</p>
        </div>
      </div>
    );
  }

  // Safely extract data with proper fallbacks
  const nvr_ip = nvrData.nvr_ip || 'N/A';
  const hdd = nvrData.hdd || {};
  const health = nvrData.health || {};
  const smart = nvrData.smart || {};
  const cameras = nvrData.cameras || {};
  const cameraList = cameras.camera_list || [];
  const recording = nvrData.recording || {};
  const last24h = recording.last_24_hours || {};
  const perCamera = last24h.per_camera || [];
  const timestamp = nvrData.timestamp;

  // Calculate used percentage (hdd.capacity / hdd.free_space are in MB)
  const capacityMb = hdd.capacity || 0;
  const freeSpaceMb = hdd.free_space || 0;
  const usedPercentage = capacityMb > 0 ? Math.round(((capacityMb - freeSpaceMb) / capacityMb) * 100) : 0;

  // --- Retention estimate ---
  // hdd.capacity is in MB, recording.last_24_hours.total_data_gb is in GB.
  // Convert capacity to GB so both sides of the division share the same unit.
  const capacityGb = capacityMb / 1024;
  const freeSpaceGb = freeSpaceMb / 1024;
  const dailyUsageGb = last24h.total_data_gb || 0;

  // Total days the full drive can hold at the current recording rate
  const totalRetentionDays = dailyUsageGb > 0 ? capacityGb / dailyUsageGb : null;
  // Days left before the drive fills up, based on space currently free
  const daysUntilFull = dailyUsageGb > 0 ? freeSpaceGb / dailyUsageGb : null;

  // Donut chart data for storage usage
  const usedGb = Math.max(capacityGb - freeSpaceGb, 0);
  const storageChartData = [
    { name: 'Used', value: Number(usedGb.toFixed(2)) },
    { name: 'Free', value: Number(freeSpaceGb.toFixed(2)) }
  ];
  const STORAGE_COLORS = usedPercentage > 90
    ? ['#ef4444', '#e2e8f0']
    : usedPercentage > 75
    ? ['#eab308', '#e2e8f0']
    : ['#22c55e', '#e2e8f0'];

  // Color palette for per-camera recording share donut
  const CAMERA_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6', '#eab308', '#6366f1', '#ef4444'];
  const cameraChartData = perCamera.map((cam) => ({
    name: cam.camera_name,
    value: cam.data_gb
  }));

  return (
    <div className="flex flex-col gap-6 p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-widest text-blue-500">
            Storage Monitor
          </span>
          <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900">
            NVR Storage Status
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Real-time monitoring of NVR hard drive health and performance
          </p>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-slate-400">
            Last updated: {timestamp ? new Date(timestamp).toLocaleDateString('en-GB') : 'N/A'}
          </span>
          <button
            onClick={fetchNvrData}
            disabled={refreshing}
            className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 text-slate-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* NVR Info Card */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-50 rounded-xl">
            <Server className="w-8 h-8 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-slate-900">NVR Device</h2>
            <div className="flex items-center gap-4 mt-1">
              <span className="text-sm text-slate-600">
                <Wifi className="inline w-4 h-4 mr-1" />
                {nvr_ip}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(health.status)}`}>
                <Circle className={`inline w-2 h-2 mr-1 fill-current`} />
                {health.status || 'Unknown'}
              </span>
              <span className="text-sm text-slate-600">
                <Camera className="inline w-4 h-4 mr-1" />
                {cameras.configured ?? cameraList.length} cameras configured
              </span>
            </div>
          </div>
        </div>

        {/* Problems */}
        {health.problems && health.problems.length > 0 && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-700">Issues Detected:</p>
                <ul className="mt-1 space-y-1">
                  {health.problems.map((problem, index) => (
                    <li key={index} className="text-sm text-red-600">• {problem}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* HDD Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* HDD Details */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <HardDrive className="w-5 h-5 text-slate-600" />
            <h3 className="text-sm font-semibold text-slate-900">Hard Drive Details</h3>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Drive Name</span>
              <span className="text-sm font-medium text-slate-900">{hdd.name || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Type</span>
              <span className="text-sm font-medium text-slate-900">{hdd.type || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Property</span>
              <span className="text-sm font-medium text-slate-900">{hdd.property || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-slate-600">Status</span>
              <div className="flex items-center gap-2">
                {getHddStatusIcon(hdd.status)}
                <span className={`text-sm font-medium capitalize ${
                  hdd.status === 'ok' ? 'text-green-600' :
                  hdd.status === 'warning' ? 'text-yellow-600' :
                  hdd.status === 'error' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {hdd.status || 'Unknown'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* SMART Information */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="w-5 h-5 text-slate-600" />
            <h3 className="text-sm font-semibold text-slate-900">SMART Health</h3>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Temperature</span>
              <div className="flex items-center gap-2">
                <Thermometer className={`w-4 h-4 ${(smart.temperature_celsius || 0) > 50 ? 'text-red-500' : 'text-blue-500'}`} />
                <span className={`text-sm font-medium ${(smart.temperature_celsius || 0) > 50 ? 'text-red-600' : 'text-slate-900'}`}>
                  {smart.temperature_celsius || 0}°C
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Power On</span>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-900">
                  {smart.power_on_days || 0} days ({smart.power_on_hours || 0} hours)
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Overall Evaluation</span>
              <span className="text-sm font-medium text-slate-900 capitalize">
                {smart.overall_evaluation || 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Self Evaluation</span>
              <span className={`text-sm font-medium capitalize ${
                smart.self_evaluation === 'ok' ? 'text-green-600' :
                smart.self_evaluation === 'warning' ? 'text-yellow-600' :
                smart.self_evaluation === 'failed' ? 'text-red-600' : 'text-gray-600'
              }`}>
                {smart.self_evaluation || 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-slate-600">Self Test</span>
              <span className="text-sm font-medium text-slate-900 capitalize">
                {(smart.self_test_status || 'not_tested').replace('_', ' ')} ({smart.self_test_percent || 0}%)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Storage, Sectors & Retention */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Storage Usage */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-5 h-5 text-slate-600" />
            <h3 className="text-sm font-semibold text-slate-900">Storage Usage</h3>
          </div>

          <div className="relative w-40 h-40 mx-auto">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={storageChartData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="70%"
                  outerRadius="100%"
                  paddingAngle={2}
                  startAngle={90}
                  endAngle={-270}
                  stroke="none"
                >
                  {storageChartData.map((entry, index) => (
                    <Cell key={entry.name} fill={STORAGE_COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [`${value} GB`, name]}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-extrabold text-slate-900">{usedPercentage}%</span>
              <span className="text-[11px] text-slate-500">used</span>
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-4 text-xs">
            <span className="flex items-center gap-1.5 text-slate-600">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STORAGE_COLORS[0] }} />
              Used
            </span>
            <span className="flex items-center gap-1.5 text-slate-600">
              <span className="w-2 h-2 rounded-full bg-slate-200" />
              Free
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 mt-4 border-t border-slate-100">
            <div>
              <p className="text-xs text-slate-500">Total Capacity</p>
              <p className="text-sm font-semibold text-slate-900">{formatBytes(hdd.capacity)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Free Space</p>
              <p className="text-sm font-semibold text-slate-900">{formatBytes(hdd.free_space)}</p>
            </div>
          </div>
        </div>

        {/* Sector Information */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-slate-600" />
            <h3 className="text-sm font-semibold text-slate-900">Sector Health</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-green-50 rounded-lg border border-green-100">
              <p className="text-xs text-slate-600">Reallocated</p>
              <p className="text-lg font-bold text-green-600">{smart.reallocated_sectors || 0}</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
              <p className="text-xs text-slate-600">Pending</p>
              <p className="text-lg font-bold text-yellow-600">{smart.pending_sectors || 0}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg border border-red-100">
              <p className="text-xs text-slate-600">Uncorrectable</p>
              <p className="text-lg font-bold text-red-600">{smart.uncorrectable_sectors || 0}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
              <p className="text-xs text-slate-600">CRC Errors</p>
              <p className="text-lg font-bold text-purple-600">{smart.crc_errors || 0}</p>
            </div>
          </div>
        </div>

        {/* Retention Estimate */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <CalendarClock className="w-5 h-5 text-slate-600" />
              <h3 className="text-sm font-semibold text-slate-900">Retention Estimate{" "}
                 <span className="text-xs font-medium text-slate-500">
                  (OVERWRITE MODE)
                </span>
              </h3>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
              daysUntilFull === null ? 'text-gray-600 bg-gray-50 border-gray-200' :
              daysUntilFull <= 2 ? 'text-red-600 bg-red-50 border-red-200' :
              daysUntilFull <= 7 ? 'text-yellow-600 bg-yellow-50 border-yellow-200' :
              'text-green-600 bg-green-50 border-green-200'
            }`}>
              {daysUntilFull === null ? 'Unavailable' :
               daysUntilFull <= 2 ? 'Critical' :
               daysUntilFull <= 7 ? 'Low' : 'Healthy'}
            </span>
          </div>

          <div className="text-center py-2">
            <p className="text-4xl font-extrabold text-slate-900">
              {daysUntilFull !== null ? daysUntilFull.toFixed(1) : '—'}
            </p>
            <p className="text-xs text-slate-500 mt-1">days of storage remaining</p>
          </div>

          <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden mt-3 mb-4">
            <div
              className={`h-full transition-all duration-500 ${
                daysUntilFull === null ? 'bg-gray-300' :
                daysUntilFull <= 2 ? 'bg-red-500' :
                daysUntilFull <= 7 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{
                width: totalRetentionDays && daysUntilFull !== null
                  ? `${Math.min(100, (daysUntilFull / totalRetentionDays) * 100)}%`
                  : '0%'
              }}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
            <div>
              <p className="text-xs text-slate-500">Recording Rate</p>
              <p className="text-sm font-semibold text-slate-900">{dailyUsageGb.toFixed(1)} GB/day</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Full Drive Capacity</p>
              <p className="text-sm font-semibold text-slate-900">
                {totalRetentionDays !== null ? `${totalRetentionDays.toFixed(0)} days` : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recording Summary - Last 24 Hours */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Video className="w-5 h-5 text-slate-600" />
            <h3 className="text-sm font-semibold text-slate-900">Recording Summary — Last 24 Hours</h3>
          </div>
          <span className="text-xs text-slate-400">
            {last24h.start_time ? new Date(last24h.start_time).toLocaleString() : 'N/A'} → {last24h.end_time ? new Date(last24h.end_time).toLocaleString() : 'N/A'}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stats + Donut */}
          <div className="lg:col-span-1">
            <div className="grid grid-cols-3 lg:grid-cols-1 gap-3 mb-4">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <p className="text-xs text-slate-500">Total Data Recorded</p>
                <p className="text-lg font-bold text-slate-900">{formatDataBytes(last24h.total_data_bytes)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <p className="text-xs text-slate-500">Total Segments</p>
                <p className="text-lg font-bold text-slate-900">{last24h.total_recording_segments ?? 0}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <p className="text-xs text-slate-500">Cameras Reporting</p>
                <p className="text-lg font-bold text-slate-900">{perCamera.length}</p>
              </div>
            </div>

            <div className="relative w-full h-52">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={cameraChartData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius="55%"
                    outerRadius="90%"
                    paddingAngle={2}
                    stroke="none"
                  >
                    {cameraChartData.map((entry, index) => (
                      <Cell key={entry.name} fill={CAMERA_COLORS[index % CAMERA_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [`${value} GB`]}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-lg font-extrabold text-slate-900">{formatDataBytes(last24h.total_data_bytes)}</span>
                <span className="text-[11px] text-slate-500">recorded</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-3">
              {cameraChartData.map((cam, index) => (
                <span key={cam.name} className="flex items-center gap-1.5 text-[11px] text-slate-600 truncate">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: CAMERA_COLORS[index % CAMERA_COLORS.length] }} />
                  <span className="truncate">{cam.name}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="lg:col-span-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-4 font-medium">Camera</th>
                  <th className="py-2 pr-4 font-medium">Track ID</th>
                  <th className="py-2 pr-4 font-medium">Segments</th>
                  <th className="py-2 pr-4 font-medium">Data Recorded</th>
                  <th className="py-2 pr-4 font-medium">Share of Total</th>
                </tr>
              </thead>
              <tbody>
                {perCamera.map((cam, index) => {
                  const share = last24h.total_data_gb > 0 ? (cam.data_gb / last24h.total_data_gb) * 100 : 0;
                  return (
                    <tr key={cam.camera_id} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-4 font-medium text-slate-900">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: CAMERA_COLORS[index % CAMERA_COLORS.length] }} />
                          {cam.camera_name}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-slate-600">{cam.track_id}</td>
                      <td className="py-2 pr-4 text-slate-600">{cam.recording_segments}</td>
                      <td className="py-2 pr-4 text-slate-600">{cam.data_gb} GB</td>
                      <td className="py-2 pr-4 text-slate-600">{share.toFixed(1)}%</td>
                    </tr>
                  );
                })}
                {perCamera.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-4 text-center text-slate-400">No recording data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Cameras */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <Camera className="w-5 h-5 text-slate-600" />
          <h3 className="text-sm font-semibold text-slate-900">Cameras ({cameras.configured ?? cameraList.length})</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {cameraList.map((cam) => (
            <div key={cam.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50">
              <div className="flex items-start justify-between">
                <p className="text-sm font-semibold text-slate-900">{cam.name}</p>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                  {cam.protocol}
                </span>
              </div>
              <div className="mt-2 space-y-1">
                <p className="text-xs text-slate-500">
                  <Wifi className="inline w-3 h-3 mr-1" />
                  {cam.ip_address}
                </p>
                <p className="text-xs text-slate-500">Model: {cam.model}</p>
                <p className="text-xs text-slate-500">Track ID: {cam.track_id}</p>
              </div>
            </div>
          ))}
          {cameraList.length === 0 && (
            <p className="text-sm text-slate-400 col-span-full text-center py-4">No cameras configured</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Nvr;